import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function IssuedProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const activeTab = location.state?.activeTab || "new";

    const [product, setProduct] = useState(location.state?.product || null);
    const [loading, setLoading] = useState(!location.state?.product);
    const [error, setError] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [timelineLoading, setTimelineLoading] = useState(true);

    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/products/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success || resData.data) {
                setProduct(resData.data || resData);
            } else {
                setError(resData.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchProductTimeline = useCallback(async () => {
        if (!id) return;
        setTimelineLoading(true);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/product/timeline/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success && resData.data && Array.isArray(resData.data.timeline)) {
                const filteredTimeline = resData.data.timeline.filter((item) => {
                    const hasValidValues = Object.values(item).some(
                        (val) => val !== null && val !== undefined && val !== ""
                    );
                    return hasValidValues;
                });
                setTimeline(filteredTimeline);
            }
        } catch (err) {
            console.error("Error fetching product timeline:", err);
        } finally {
            setTimelineLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
        if (id) {
            fetchProductTimeline();
        }
    }, [id, product, fetchProductById, fetchProductTimeline]);

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate(`/issue-product-list?tab=${activeTab}`)}
                        >
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("issued product details")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/issue-product-list?tab=${activeTab}`);
                                            }}
                                            className="mc-breadcrumb-link"
                                        >
                                            {t("issued products")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {t("product details")}
                                    </li>
                                </ul>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(`/issue-product-list?tab=${activeTab}`)}
                            >
                                {t("back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                {/* Left Side: Product Information */}
                <Col lg={6}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("product information")}</h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                            {product.first_img ? (
                                <img
                                    src={`${process.env.REACT_APP_IMG_URL}${product.first_img}`}
                                    alt={product.title}
                                    className="img-fluid rounded mb-3"
                                    style={{ maxHeight: "220px", objectFit: "cover" }}
                                />
                            ) : (
                                <div
                                    className="bg-light d-flex align-items-center justify-content-center rounded mb-3"
                                    style={{ height: "180px" }}
                                >
                                    <span className="text-muted">{t("no image")}</span>
                                </div>
                            )}
                            <h5>{product.title || "N/A"}</h5>
                            <Badge bg="primary" className="mb-3">
                                {product.product_code || "N/A"}
                            </Badge>
                        </Card.Body>
                        <Card.Body className="border-top">
                            <Row className="g-3">
                                <Col sm={6}>
                                    <strong>{t("category")}: </strong>
                                    <span>
                                        {typeof product.category === "object"
                                            ? product.category?.name
                                            : product.category || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("brand")}: </strong>
                                    <span>
                                        {typeof product.brand === "object"
                                            ? product.brand?.name
                                            : product.brand || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("lister")}: </strong>
                                    <span>
                                        {typeof product.lister === "object"
                                            ? `${product.lister?.name || "N/A"}${product.lister?.nickname
                                                ? ` (${product.lister.nickname})`
                                                : ""
                                            }`
                                            : product.lister_name || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("store")}: </strong>
                                    <span>
                                        {typeof product.store === "object"
                                            ? product.store?.name
                                            : product.store || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("market placer")}: </strong>
                                    <span>
                                        {typeof product.marketPlacer === "object"
                                            ? `${product.marketPlacer?.name || "N/A"}${product.marketPlacer?.nickname
                                                ? ` (${product.marketPlacer.nickname})`
                                                : ""
                                            }`
                                            : product.marketPlacer || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("condition")}: </strong>
                                    <span>{product.product_condition || "N/A"}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("selling style")}: </strong>
                                    <span>{product.selling_style || "N/A"}</span>
                                </Col>
                                
                                {product.auction_min_price != null && (
                                    <Col sm={6}>
                                        <strong>{t("auction min price")}: </strong>
                                        <span>{product.auction_min_price}</span>
                                    </Col>
                                )}

                                <Col sm={6}>
                                    <strong>{t("quantity")}: </strong>
                                    <span>{product.product_qty ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("short buying price")}: </strong>
                                    <span>
                                        {product.short_buying_price !== null && product.short_buying_price !== undefined
                                            ? `${product.short_buying_price}`
                                            : "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("Buying price PKR")}: </strong>
                                    <span>{product.buying_price ?? product.price ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("sell price dollar")}: </strong>
                                    <span>
                                        {Number(product.sell_price_dollar) === 0
                                            ? t("Study")
                                            : `$${product.sell_price_dollar}`}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("shipping charges dollar")}: </strong>
                                    <span>${product.shipping_charges_dollar ?? 0}</span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Side: Product Timeline with Handover Image Support */}
                <Col lg={6}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("Product Timeline")}</h5>
                        </Card.Header>
                        <Card.Body>
                            {timelineLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" variant="primary" />
                                    <span className="ms-2 text-muted">{t("Loading timeline...")}</span>
                                </div>
                            ) : timeline.length > 0 ? (
                                <div style={{ position: "relative", paddingLeft: "30px" }}>
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: "9px",
                                            top: "5px",
                                            bottom: "5px",
                                            width: "2px",
                                            backgroundColor: "#e9ecef",
                                        }}
                                    />
                                    {timeline.map((item, index) => (
                                        <div key={index} style={{ position: "relative", marginBottom: "20px" }}>
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    left: "-25px",
                                                    top: "3px",
                                                    width: "12px",
                                                    height: "12px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#0d6efd",
                                                    border: "2px solid #fff",
                                                    boxShadow: "0 0 0 2px #0d6efd",
                                                }}
                                            />
                                            <div className="d-flex justify-content-between align-items-center">
                                                <strong className="text-dark">{item.title || "N/A"}</strong>
                                                <small className="text-muted">{item.time || ""}</small>
                                            </div>
                                            {/* {item.status && (
                                                <div className="small text-secondary mt-1">
                                                    Status: <Badge bg="light" text="dark" className="border">{item.status}</Badge>
                                                </div>
                                            )}
                                            {item.movement_type && (
                                                <div className="small text-muted mt-1">
                                                    Movement: {item.movement_type}
                                                </div>
                                            )} */}

                                            {/* Render handover_image if present in the timeline item */}
                                            {item.handover_image && (
                                                <div className="mt-2">
                                                    <span className="small text-muted d-block mb-1">{t("Handover Image")}:</span>
                                                    <a
                                                        href={`${process.env.REACT_APP_IMG_URL}${item.handover_image}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={`${process.env.REACT_APP_IMG_URL}${item.handover_image}`}
                                                            alt="Handover"
                                                            className="img-fluid rounded border"
                                                            style={{ maxHeight: "120px", objectFit: "cover" }}
                                                        />
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted mb-0">{t("No timeline data available")}</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}